import { AxiosError } from 'axios';
import classNames from 'classnames';
import { isEqual } from 'lodash';
import React, {
  useState, useEffect, useMemo, useCallback,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Row, Col, Accordion, AccordionBody, AccordionHeader,
  AccordionItem, Nav, NavItem, NavLink, TabContent, TabPane,
  Badge,
} from 'reactstrap';

import { upperPermanent, lowerPermanent } from '@config/teeth-data/permanent';
import { upperPrimary, lowerPrimary } from '@config/teeth-data/primary';
import {
  markAsUrgentDispatch,
} from '@reducers/referral/ReferralAction';
import { handleError } from '@services/ErrorHandler';
import LocaleService from '@services/LocaleService';
import { CustomButton } from '@shared/buttons';
import Alert from '@shared/components/alert/Alert';
import CustomWysiwyg from '@shared/components/custom-wysiwyg/CustomWysiwyg';
import {
  cleanText, stripHtmlTags, trimWysiwygValue,
} from '@shared/helpers';
import useFormPrompt from '@shared/hooks/useFormPrompt';
import {
  SavedIcon, ToothCloseIcon, InfoFilledIcon,
} from '@shared/icons';

import { PendingSaveResult } from './ReferralInformationForm';
import SpecialtiesServices from './specialties-services/SpecialtiesServices';
import { useGuestReferral, useReferralStepRequests } from '../guest/GuestReferralContext';

import '../SendReferralOrderPageStyle.scss';

const maxLength = 5000;

type ReasonForReferralFormProps = {
  open: string;
  toggle: (e: any) => void;
  loadingDetails: boolean;
  persistPendingReferralInfo?: (
    latestReferral: any,
    options?: { reopenOnFailure?: boolean },
  ) => Promise<PendingSaveResult>;
};

const ReasonForReferralForm = ({
  open, toggle, loadingDetails, persistPendingReferralInfo,
}: ReasonForReferralFormProps) => {
  const dispatch = useDispatch<any>();
  const location = useLocation();
  const navigate = useNavigate();
  const requests = useReferralStepRequests();
  const guest = useGuestReferral();
  const isUpdate = location?.pathname?.includes('view');
  const returnTo = location?.state?.returnTo;
  const i18n = LocaleService.getTranslations('referrals.sendReferral');
  const i18nReferralReason = LocaleService.getTranslations('referrals.sendReferral.forms.reasonForReferral');
  const referralDetails = useSelector(({ Referral }) => Referral.referralData);
  const isUrgent = useSelector(({ Referral }) => Referral.isUrgent);
  const isCompleted = referralDetails?.status === 'completed';
  const [ activeTab, setActiveTab ] = useState('permanent');
  const [ loading, setLoading ] = useState(false);
  const [ errors, setErrors ] = useState<any>([]);
  const [ saved, setSaved ] = useState(false);
  const [ invalid, setInvalid ] = useState(false);
  const [ formData, setFormData ] = useState<any>({
    remarks: '',
    is_urgent: isUrgent ?? referralDetails?.is_urgent,
  });
  const [ selectedTeeth, setSelectedTeeth ] = useState<Array<{ tooth: string, type: string }>>([]);
  const [ selectedSpecialties, setSelectedSpecialties ] = useState<any[]>([]);
  const [ initialSpecialties, setInitialSpecialties ] = useState<any[]>([]);
  const [ uncheckedSpecialties, setUncheckedSpecialties ] = useState([]);
  const [ newCheckedSpecialties, setNewCheckedSpecialties ] = useState([]);
  const [ isSaving, setIsSaving ] = useState(false);

  const initialData = useMemo(() => {
    if (open !== '4') return {}; // Return empty object if accordion is closed

    return {
      referral_id: referralDetails?.id ?? '',
      patient_id: referralDetails?.patient?.id ?? '',
      tooth: referralDetails?.tooth ?? [],
      referral_services: initialSpecialties ?? [],
      is_urgent: referralDetails?.is_urgent || false,
      remarks: referralDetails?.remarks ?? '',
    };
  }, [
    open,
    initialSpecialties,
    referralDetails,
  ]);

  const isDirty = useCallback(() => {
    if (open !== '4') return false; // Skip the check if accordion is closed
    const newData = {
      referral_id: referralDetails?.id ?? '',
      patient_id: referralDetails?.patient?.id ?? '',
      tooth: selectedTeeth ?? [],
      referral_services: selectedSpecialties ?? [],
      is_urgent: isUrgent || referralDetails?.is_urgent || false,
      remarks: formData?.remarks || referralDetails?.remarks || '',
    };

    return !isEqual(newData, initialData);
  }, [
    initialData, formData, open,
    selectedTeeth, selectedSpecialties,
    referralDetails, isUrgent,
  ]);

  const handleToothClick = (tooth: string, type: string) => {
    setSelectedTeeth((prevSelectedTeeth) => {
      const toothObject = { tooth, type };
      const isExists = prevSelectedTeeth.some((t) => t.tooth === tooth && t.type === type);

      if (isExists) {
        // Remove the tooth object
        return prevSelectedTeeth.filter((t) => !(t.tooth === tooth && t.type === type));
      }
      // Add the tooth object
      return [ ...prevSelectedTeeth, toothObject ];
    });
  };

  const removeTooth = (tooth: string, type: string) => {
    setSelectedTeeth((prevSelectedTeeth) => prevSelectedTeeth
      .filter((t) => !(t.tooth === tooth && t.type === type)),
    );
  };

  // sun editor input change
  const handleInputChange = (value: any) => {
    let newValue = value;
    // Trim leading and trailing spaces/tags from the value
    newValue = newValue.trim();
    newValue = trimWysiwygValue(newValue);

    setFormData((prevState: any) => ({
      ...prevState,
      remarks: cleanText(newValue), // Remove invisible zero-width characters (occurs from pasting)
    }));
  };

  const saveReferralReason = async (shouldGoNext: boolean) => {
    if (!isDirty()) {
      setSaved(true);
      if (shouldGoNext) toggle('5');
      return;
    }
    setLoading(true);
    setInvalid(false);
    setErrors([]);
    // message will prompt that the user should
    // fill out Referral Information (step 1) first, unless step 1 is only
    // waiting for the draft (reached without Patient History's Next).
    if (!referralDetails?.referred_to?.id) {
      const pending = (await persistPendingReferralInfo?.(referralDetails, {
        reopenOnFailure: shouldGoNext,
      })) ?? 'none';
      if (pending !== 'saved') {
        setInvalid(true);
        setLoading(false);
        return;
      }
    }

    try {
      const countRemarksString = stripHtmlTags(formData.remarks
        || referralDetails?.remarks)?.length;

      const newData = {
        referral_id: referralDetails?.id,
        patient_id: referralDetails?.patient?.id,
        tooth: selectedTeeth,
        // referral_services: selectedSpecialties,
        new_referral_services: newCheckedSpecialties,
        removed_referral_services: uncheckedSpecialties,
        is_urgent: isUrgent || referralDetails?.is_urgent || false,
        remarks: countRemarksString > 0 ? (formData.remarks || referralDetails?.remarks) : '',
      };
      await dispatch(requests.saveReferralReasonRequest(newData)).$promise;
      setSaved(true);
      if (shouldGoNext) toggle('5');
    } catch (error) {
      const { response: errorResponse } = error as AxiosError;

      if (errorResponse) {
        if (errorResponse.status === 422) {
          const { errors: errorData } = errorResponse.data;
          setErrors(errorData);
          return;
        }
        handleError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const onHandleNext = async () => {
    await saveReferralReason(true);
  };

  const onSaveExit = async (e?: any) => {
    e?.stopPropagation();
    if (!isDirty()) {
      navigate(returnTo);
      return;
    }
    setIsSaving(true);
    setInvalid(false);
    setErrors([]);
    // message will prompt that the user should
    // fill out Referral Information (step 1) first, unless step 1 is only
    // waiting for the draft (reached without Patient History's Next).
    if (!referralDetails?.referred_to?.id) {
      const pending = (await persistPendingReferralInfo?.(referralDetails)) ?? 'none';
      if (pending !== 'saved') {
        setInvalid(true);
        setIsSaving(false);
        return;
      }
    }
    try {
      const countRemarksString = stripHtmlTags(formData.remarks
        || referralDetails?.remarks)?.length;

      const newData = {
        referral_id: referralDetails?.id,
        patient_id: referralDetails?.patient?.id,
        tooth: selectedTeeth,
        // referral_services: selectedSpecialties,
        new_referral_services: newCheckedSpecialties,
        removed_referral_services: uncheckedSpecialties,
        is_urgent: isUrgent || referralDetails?.is_urgent || false,
        remarks: countRemarksString > 0 ? (formData.remarks || referralDetails?.remarks) : '',
      };
      await dispatch(requests.saveReferralReasonRequest(newData)).$promise;
      setSaved(true);
      navigate(returnTo);
    } catch (error) {
      const { response: errorResponse } = error as AxiosError;

      if (errorResponse) {
        if (errorResponse.status === 422) {
          const { errors: errorData } = errorResponse.data;
          setErrors(errorData);
          return;
        }
        handleError(error);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const onDiscardChanges = () => {
    dispatch(markAsUrgentDispatch(referralDetails?.is_urgent || false));

    setSelectedTeeth(referralDetails?.tooth);

    if (!referralDetails?.services || referralDetails?.services?.length === 0) {
      setSelectedSpecialties([]);
      setInitialSpecialties([]);
      return;
    }
    const specialtiesWithRemarks = referralDetails.services.reduce((acc: any, service: any) => {
      if (service.remarks) {
        acc[service.specialties_id] = service.remarks;
      }
      return acc;
    }, {});

    const newSelectedSpecialties = referralDetails?.services.map((refSrvc: any) => ({
      ...refSrvc,
      specialties_name: refSrvc.details ? refSrvc.details.name : refSrvc.specialties,
      specialties_parent_id: refSrvc.details ? refSrvc.details.parent_id : refSrvc.parent_id,
      specialties_id: refSrvc.details ? refSrvc.details.id : refSrvc?.id,
    }));

    setFormData({
      ...formData,
      ...specialtiesWithRemarks,
      is_urgent: isUrgent || referralDetails?.is_urgent || false,
    });

    setSelectedSpecialties(newSelectedSpecialties);
    setInitialSpecialties(newSelectedSpecialties);
    setUncheckedSpecialties([]);
    setNewCheckedSpecialties([]);
  };

  const { confirm } = useFormPrompt({
    onSave: async () => {
      // Save form logic
      await saveReferralReason(false);
    },
    onDiscard: () => {
      // Discard changes logic
      onDiscardChanges();
    },
    // isFormDirty: isDirty,
    isFormDirty: isDirty(),
  });

  const toggleAccordion = async () => {
    // Call confirm prompt before closing accordion
    // if there are any changes
    if (open === '4' && isDirty()) {
      const result = await confirm();
      if (!result) return;
    }
    toggle('4');
  };

  const onHandleBack = async () => {
    if (isDirty()) {
      const result = await confirm();
      if (!result) return;
    }
    // A guest (ENH-052) has no page to return to: Back is the previous step.
    if (guest) {
      toggle('3');
      return;
    }
    navigate(returnTo);
  };

  // const handleUrgentClick = (e: any) => {
  //   e.stopPropagation(); // Stop event propagation
  //   setFormData({ ...formData, is_urgent: !formData?.is_urgent });
  // };

  useEffect(() => {
    const initiate = () => {
      if (!referralDetails?.tooth || open !== '4') return;
      setSelectedTeeth(referralDetails?.tooth);
    };

    initiate();

    // clear data when component unmounts
    return () => {
      setSelectedTeeth([]);
      setSelectedSpecialties([]);
      setInitialSpecialties([]);
      setInvalid(false);
      setUncheckedSpecialties([]);
      setNewCheckedSpecialties([]);
    };
  }, [ referralDetails?.tooth, open ]);

  useEffect(() => {
    const initiate = () => {
      if (!referralDetails?.services || referralDetails?.services?.length === 0 || open !== '4') return;
      const specialtiesWithRemarks = referralDetails.services.reduce((acc: any, service: any) => {
        if (service.remarks) {
          acc[service.specialties_id] = service.remarks;
        }
        return acc;
      }, {});

      const newSelectedSpecialties = referralDetails?.services.map((refSrvc: any) => ({
        ...refSrvc,
        specialties_name: refSrvc.details ? refSrvc.details.name : refSrvc.specialties,
        specialties_parent_id: refSrvc.details ? refSrvc.details.parent_id : refSrvc.parent_id,
        specialties_id: refSrvc.details ? refSrvc.details.id : refSrvc?.id,
      }));

      setFormData({
        ...formData,
        ...specialtiesWithRemarks,
        is_urgent: referralDetails.is_urgent || isUrgent,
      });

      setSelectedSpecialties(newSelectedSpecialties);
      setInitialSpecialties(newSelectedSpecialties);
    };

    initiate();

    // clear data when component unmounts
    return () => {
      // setSelectedSpecialties([]);
      // setFormData({
      //   // remarks: '',
      //   is_urgent: false,
      // });
      setInvalid(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ referralDetails?.services, open ]);

  return (
    <div>
      <Accordion
        open={open}
        toggle={() => { }}
        className={classNames('mb-3', {
          'sr-accordion-collapsed': open === '4',
        })}
      >
        <AccordionItem>
          <AccordionHeader
            className="sr-accordion-header"
            targetId="4"
            onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
              const target = e.target as Element;
              if (target.closest('.custom-checkbox')) {
                return; // Ignore the click event if it originated from the checkbox
              }
              e.preventDefault();
              toggleAccordion();
            }}
          >
            <Row className="align-items-center">
              <Col xs={2} md={1} style={{ width: '50px' }}>
                {
                  (saved || (referralDetails?.services && referralDetails?.services.length > 0)) && open !== '4' ? (<SavedIcon />) : (
                    <span
                      className={classNames('sr-accordion-number', {
                        'sr-accordion-number-collapsed': open === '4',
                      })}
                    >
                      4
                    </span>
                  )
                }
              </Col>
              <Col
                xs={9}
                md={!isCompleted && isUpdate && open === '4' ? 7 : 9}
                lg={!isCompleted && isUpdate && open === '4' ? 8 : 8}
                xl={!isCompleted && isUpdate && open === '4' ? 9 : 8}
              // xs={6}
              // sm={7}
              // md={!isCompleted && isUpdate && open === '4' ? 5 : 9}
              // lg={!isCompleted && isUpdate && open === '4' ? 5 : 8}
              // xl={!isCompleted && isUpdate && open === '4' ? 6 : 8}
              >
                <span className="sr-accordion-title">
                  {i18nReferralReason.title}
                </span>
              </Col>
              {/* <Col xs={1} xl={1} /> */}
              {/* <Col
              xs={!isCompleted && isUpdate && open === '4' ? 2 : 3}
              sm={3} md={2} lg={3}
              className="checkbox-urgent-container"
              >
                <Checkbox
                  id="is-urgent"
                  name="is-urgent"
                  type="checkbox"
                  formGrpClassName="urgent-group"
                  label={(
                    <>
                      <span className="urgent-label">
                      {i18nReferralReason.urgent}
                      </span> <UrgentFormFlagIcon />
                    </>
                  )}
                  checked={formData?.is_urgent || referralDetails?.is_urgent}
                  onInputChangeHandler={(e: any) => handleUrgentClick(e)}
                  isDisabled={isCompleted}
                />
              </Col> */}
              {!isCompleted && isUpdate && open === '4' && (
                <Col xs={12} md={4} lg={3} xl={2} className="sr-save-exit-container">
                  <CustomButton
                    id="btn-save-exit"
                    className="btn-lg sr-save-exit-btn"
                    color="primary"
                    onClick={(e: any) => onSaveExit(e)}
                    disabled={loading || loadingDetails || isSaving}
                    loading={isSaving}
                  >
                    {i18n.button.saveExit}
                  </CustomButton>
                </Col>
              )}
            </Row>
          </AccordionHeader>
          <AccordionBody accordionId="4">
            {invalid && (
              <Alert color="error">{LocaleService.parseTranslation(i18n.invalid, {
                ':number': 1,
              })}
              </Alert>
            )}
            <Row>
              <Col xs={1} style={{ width: '50px' }} />
              <Col xs={12} lg={11}>
                <div className="mb-4">
                  <div className="ref-reason-tabs-container">
                    <Row className="mb-3">
                      <Col className="text-center">
                        <Nav tabs className="ref-reason-custom-nav-tab mb-3">
                          <NavItem>
                            <NavLink
                              className={classNames('ref-reason-custom-nav-link', {
                                'ref-reason-custom-nav-link-active': activeTab === 'permanent',
                              })}
                              onClick={() => setActiveTab('permanent')}
                            >
                              {i18nReferralReason.tabs.permanent.label}
                            </NavLink>
                          </NavItem>
                          <NavItem className="ref-reason-custom-nav-item">
                            <NavLink
                              className={classNames('ref-reason-custom-nav-link', {
                                'ref-reason-custom-nav-link-active': activeTab === 'primary',
                              })}
                              onClick={() => setActiveTab('primary')}
                            >
                              {i18nReferralReason.tabs.primary.label}
                            </NavLink>
                          </NavItem>
                        </Nav>
                        <TabContent activeTab={activeTab} className="mt-5">
                          <TabPane tabId="permanent">
                            <Row className="permanent-upper-teeth-tab-pane">
                              {upperPermanent.map((u: any, index: number) => (
                                <Col
                                  key={`${index.toString()}`}
                                  className={classNames('permanent-teeth-style', {
                                    'permanent-teeth-border': index + 1 === 8,
                                    'selected-tooth-style': selectedTeeth?.length > 0 && selectedTeeth.some((t) => t.tooth === u.value),
                                    'permanent-teeth-disabled': isCompleted,
                                  })}
                                  onClick={() => (isCompleted ? undefined
                                    : handleToothClick(u.value, 'permanent'))}
                                >
                                  <div className="icon-container">{u.icon}</div>
                                  <div className="number-style">{u.label}</div>
                                </Col>
                              ))}
                            </Row>
                            <Row className="permanent-lower-teeth-tab-pane">
                              {lowerPermanent.map((u: any, index: number) => (
                                <Col
                                  key={`${index.toString()}`}
                                  className={classNames('permanent-teeth-style', {
                                    'permanent-teeth-border': index + 1 === 8,
                                    'selected-tooth-style': selectedTeeth?.length > 0 && selectedTeeth.some((t) => t.tooth === u.value),
                                    'permanent-teeth-disabled': isCompleted,
                                  })}
                                  onClick={() => (isCompleted ? undefined
                                    : handleToothClick(u.value, 'permanent'))}
                                >
                                  <div className="number-style">{u.label}</div>
                                  <div className="icon-container">{u.icon}</div>
                                </Col>
                              ))}
                            </Row>
                          </TabPane>
                          <TabPane tabId="primary">
                            <Row className="primary-upper-teeth-tab-pane">
                              {upperPrimary.map((u: any, index: number) => (
                                <Col
                                  key={`${index.toString()}`}
                                  className={classNames('primary-teeth-style', {
                                    'primary-teeth-border': index + 1 === 5,
                                    'selected-tooth-style': selectedTeeth?.length > 0 && selectedTeeth.some((t) => t.tooth === u.value),
                                    'primary-teeth-disabled': isCompleted,
                                  })}
                                  onClick={() => (isCompleted ? undefined
                                    : handleToothClick(u.value, 'primary'))}
                                >
                                  <div className="icon-container">{u.icon}</div>
                                  <div className="number-style">{u.label}</div>
                                </Col>
                              ))}
                            </Row>
                            <Row className="primary-lower-teeth-tab-pane">
                              {lowerPrimary.map((u: any, index: number) => (
                                <Col
                                  key={`${index.toString()}`}
                                  className={classNames('primary-teeth-style', {
                                    'primary-teeth-border': index + 1 === 5,
                                    'selected-tooth-style': selectedTeeth?.length > 0 && selectedTeeth.some((t) => t.tooth === u.value),
                                    'primary-teeth-disabled': isCompleted,
                                  })}
                                  onClick={() => (isCompleted ? undefined
                                    : handleToothClick(u.value, 'primary'))}
                                >
                                  <div className="number-style">{u.label}</div>
                                  <div className="icon-container">{u.icon}</div>
                                </Col>
                              ))}
                            </Row>
                          </TabPane>
                        </TabContent>
                        <div className="choose-tooth">
                          {i18nReferralReason.chooseTooth}
                        </div>
                      </Col>
                    </Row>
                  </div>
                  <div className="selected-tooth-container">
                    <Row>
                      <Col className="selected-tooth-col-style">
                        {selectedTeeth?.length > 0 && selectedTeeth
                          .map((t: any, index: number) => (
                            <Badge
                              key={`${index.toString()}`}
                              pill
                              className="tooth-badge-style"
                            >
                              <div className="badge-value-container">
                                <span className="tooth-value">{t.tooth}</span>
                                <span
                                  className={classNames('remove-tooth-style', {
                                    'remove-tooth-disabled': isCompleted,
                                  })}
                                  onClick={() => (isCompleted ? undefined
                                    : removeTooth(t.tooth, t.type))}
                                >
                                  <ToothCloseIcon />
                                </span>
                              </div>
                            </Badge>
                          ))}
                      </Col>
                    </Row>
                  </div>
                  {/* {selectedTeeth.length > 0 && (
                    <SpecialtiesServices
                      selectedSpecialties={selectedSpecialties}
                      setSelectedSpecialties={setSelectedSpecialties}
                      formData={formData}
                      setFormData={setFormData}
                      errors={errors}
                      isCompleted={isCompleted}
                    />
                  )} */}
                  <SpecialtiesServices
                    selectedSpecialties={selectedSpecialties}
                    setSelectedSpecialties={setSelectedSpecialties}
                    setUncheckedSpecialties={setUncheckedSpecialties}
                    setNewCheckedSpecialties={setNewCheckedSpecialties}
                    initialSpecialties={initialSpecialties}
                    formData={formData}
                    setFormData={setFormData}
                    errors={errors}
                    isCompleted={isCompleted}
                    open={open}
                  />
                </div>
                <div className="dotted-border mb-4" />
                {!loadingDetails && (
                  <div className="mb-1">
                    <CustomWysiwyg
                      content={formData?.remarks || referralDetails?.remarks}
                      inputChange={handleInputChange}
                      errors={errors?.remarks}
                      placeholder={i18nReferralReason.remarks.placeholder}
                      maxLength={maxLength}
                      readOnly={isCompleted}
                    />
                  </div>
                )}
                {!isCompleted && (
                  <div className="mb-4">
                    <div className="mb-3 sr-info-style">
                      <InfoFilledIcon />
                      <span className="sr-info-style-text">{i18n.info}</span>
                    </div>
                    <div className="sr-footer-btn-container">
                      <CustomButton
                        id="btn-back"
                        className="btn-lg sr-back-btn"
                        color="secondary"
                        onClick={onHandleBack}
                        disabled={loading || loadingDetails}
                      >
                        {i18n.button.back}
                      </CustomButton>
                      <CustomButton
                        id="btn-next"
                        className="btn-lg sr-next-btn"
                        color="primary"
                        onClick={onHandleNext}
                        disabled={loading
                          || stripHtmlTags(formData?.remarks).length > maxLength}
                        loading={loading}
                      >
                        {i18n.button.next}
                      </CustomButton>
                    </div>
                  </div>
                )}
              </Col>
            </Row>
          </AccordionBody>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

export default ReasonForReferralForm;
